import {
  getAssignedSurvey,
  getCourseById,
  getSurveys,
  orEmptyArray,
  orUndefined,
} from '@lms/api-client/backend';
import { LoaderCircle } from 'lucide-react';
import { useParams } from 'react-router-dom';
import useSWR from 'swr';
import { useTranslations } from 'use-intl';
import { PageBox, PageHeader, PageTitle } from '@/components/admin/page-box';
import { useSiteAccess } from '@/features/auth/use-auth';
import { useBrand } from '@/features/branding/brand-provider';
import { CourseView } from '@/features/courses/components/course-view';
import { NotFound } from '@/routes/not-found';

// Ports app/[brand]/admin/courses/[id]/page.tsx. The Next server component
// awaited the course, surveys and assigned survey and used `notFound()` on a
// bad id; here everything loads via SWR, the id comes from `useParams`, and an
// invalid/missing course renders <NotFound />. Editing is super-admin only (the
// Next app only surfaced the manage entry point to super-admins).
export function CourseDetail() {
  const t = useTranslations();
  const { brand: site, slug } = useBrand();
  const { access, loading: roleLoading } = useSiteAccess(slug);
  const { id: idParam } = useParams<{ id: string }>();

  const id = Number.parseInt(idParam ?? '', 10);
  const validId = !Number.isNaN(id);
  const isSuperAdmin = access?.superAdmin ?? false;

  const { data: course, isLoading: courseLoading } = useSWR(
    validId && isSuperAdmin ? ['admin/course', id] : null,
    () => orUndefined(getCourseById(id)),
  );
  const { data: surveys } = useSWR(
    isSuperAdmin ? ['admin/surveys'] : null,
    () => orEmptyArray(getSurveys()),
  );
  const { data: assignedSurvey } = useSWR(
    validId && isSuperAdmin ? ['admin/assigned-survey', id] : null,
    () => orUndefined(getAssignedSurvey(id, { skipAuthRedirect: true })),
  );

  if (roleLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoaderCircle className="text-primary size-8 animate-spin" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <PageBox>
        <PageHeader backHref={`/${slug}/admin/courses`}>
          <PageTitle>{t('addcourse.courses')}</PageTitle>
        </PageHeader>
        <p className="text-muted-foreground">{t('admin.unauthorized')}</p>
      </PageBox>
    );
  }

  if (!validId) {
    return <NotFound />;
  }

  if (courseLoading || surveys === undefined) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoaderCircle className="text-primary size-8 animate-spin" />
      </div>
    );
  }

  if (!course) {
    return <NotFound />;
  }

  return (
    <CourseView
      site={site}
      course={course}
      surveys={surveys}
      assignedSurvey={assignedSurvey}
    />
  );
}
